import { motion } from "framer-motion";
import { Books, ArrowSquareOut, Clock, BookOpen } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AMAZON_BOOKS, AMAZON_STORE_URL, AMAZON_SERIES_URL, amazonUrl } from "@/data/storeLinks";

const FormatBadge = () => (
  <span className="inline-flex items-center gap-1 bg-amber-100 text-[#17284b] border border-[#d4dff0] rounded-full px-2.5 py-0.5 font-nunito font-bold text-[11px] uppercase tracking-wider">
    <Books size={12} weight="bold" /> Printed Paperback
  </span>
);

const BookCard = ({ book, i }) => {
  const url = amazonUrl(book);
  return (
    <motion.article
      className="bg-white border border-[#d4dff0] rounded-3xl overflow-hidden flex flex-col"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.05 }}
      data-testid={`amazon-book-${book.id}`}
    >
      <div className="bg-[#fffdf7] border-b border-[#d4dff0] p-4 flex items-center justify-center">
        {book.cover ? (
          <img src={book.cover} alt={`${book.title} — paperback cover`} className="h-56 sm:h-64 w-auto object-contain rounded-lg border border-[#d4dff0] bg-white" loading="lazy" />
        ) : (
          <div className="h-56 sm:h-64 aspect-[17/22] rounded-lg border-2 border-dashed border-slate-400 bg-white flex flex-col items-center justify-center gap-2 text-slate-500" aria-label="Cover coming soon">
            <BookOpen size={36} weight="duotone" />
            <span className="font-nunito font-bold text-xs text-center px-3">Cover coming soon</span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <FormatBadge />
          <span className="font-nunito font-bold text-xs text-[#596882] bg-muted rounded-full px-2.5 py-0.5">{book.grades}</span>
        </div>
        <h3 className="font-nunito font-extrabold tracking-tight text-base sm:text-lg text-[#17284b] leading-snug">{book.title}</h3>
        <p className="font-nunito text-sm text-[#596882] leading-relaxed flex-1">{book.subtitle}</p>
        {book.comingSoon ? (
          <span
            className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-dashed border-slate-400 bg-slate-50 font-nunito font-bold text-sm text-[#596882] cursor-default"
            aria-disabled="true"
            data-testid={`amazon-coming-soon-${book.id}`}
          >
            <Clock size={16} weight="bold" /> Amazon Edition — Coming Soon
          </span>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`amazon-buy-${book.id}`}>
            <Button className="w-full bg-[#ff8518] hover:bg-[#e8740f] text-white font-nunito font-bold text-base border border-[#d4dff0] rounded-xl py-5">
              Buy on Amazon <ArrowSquareOut size={16} weight="bold" className="ml-2" />
            </Button>
          </a>
        )}
      </div>
    </motion.article>
  );
};

export const AmazonBooks = () => {
  const published = AMAZON_BOOKS.filter((b) => !b.comingSoon);
  const upcoming = AMAZON_BOOKS.filter((b) => b.comingSoon);
  return (
    <section id="amazon" className="py-14 sm:py-20 px-4 bg-[#fff5e9] border-y border-[#d4dff0] scroll-mt-32" aria-labelledby="amazon-title">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#7358b3] inline-block">Printed &amp; shipped</span>
            <h2 id="amazon-title" className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl text-[#17284b] mt-1">Shop Our Printed Workbooks on Amazon</h2>
            <p className="font-nunito text-base sm:text-lg text-[#596882] mt-3">
              {published.length} paperback workbooks for home, classroom, homeschool, tutoring, and repeated practice — the same
              structured pages, printed and delivered. Paperbacks ship from Amazon; digital printables are on TpT.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={AMAZON_SERIES_URL} target="_blank" rel="noopener noreferrer" data-testid="amazon-series-btn">
              <Button variant="outline" className="bg-white text-[#17284b] font-nunito font-bold border border-[#d4dff0] rounded-full">
                Browse the series <ArrowSquareOut size={16} weight="bold" className="ml-2" />
              </Button>
            </a>
            <a href={AMAZON_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="amazon-author-btn">
              <Button variant="outline" className="bg-white text-[#17284b] font-nunito font-bold border border-[#d4dff0] rounded-full">
                Author page <ArrowSquareOut size={16} weight="bold" className="ml-2" />
              </Button>
            </a>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="amazon-books-grid">
          {published.map((b, i) => <BookCard key={b.id} book={b} i={i} />)}
        </div>

        {upcoming.length > 0 && (
          <div className="mt-10">
            <h3 className="font-nunito font-extrabold tracking-tight text-xl text-[#17284b] mb-4 flex items-center gap-2">
              <Clock size={20} weight="bold" className="text-[#7358b3]" /> Coming soon to Amazon
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="amazon-upcoming-grid">
              {upcoming.map((b, i) => <BookCard key={b.id} book={b} i={i} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
